import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import {
  TreePine,
  Droplets,
  Flower2,
  Shovel,
  Paintbrush,
  Leaf,
  Building,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";

// Import service images
import servicePressureWashing from "@/assets/service-pressure-washing.webp";
import serviceSoftWash from "@/assets/service-soft-wash.webp";
import serviceRoofCleaning from "@/assets/service-roof-cleaning.webp";
import serviceWindowCleaning from "@/assets/service-window-cleaning.webp";
import serviceDriveway from "@/assets/service-driveway.webp";
import serviceDeckFence from "@/assets/service-deck-fence.webp";
import servicePressureWashing400 from "@/assets/service-pressure-washing-400.webp";
import serviceSoftWash400 from "@/assets/service-soft-wash-400.webp";
import serviceRoofCleaning400 from "@/assets/service-roof-cleaning-400.webp";
import serviceWindowCleaning400 from "@/assets/service-window-cleaning-400.webp";
import serviceDriveway400 from "@/assets/service-driveway-400.webp";
import serviceDeckFence400 from "@/assets/service-deck-fence-400.webp";

const services = [
  {
    icon: TreePine,
    title: "Landscape Design & Installation",
    description: "Custom landscape plans tailored to your property",
    benefit: "Transform your outdoor space with professional design",
    image: servicePressureWashing,
    image400: servicePressureWashing400,
    slug: "landscape-design",
    featured: true,
  },
  {
    icon: Droplets,
    title: "Irrigation Design & Installation",
    description: "Efficient irrigation systems for healthy landscapes",
    benefit: "Save water with smart irrigation solutions",
    image: serviceSoftWash,
    image400: serviceSoftWash400,
    slug: "irrigation",
  },
  {
    icon: Shovel,
    title: "Drainage Services",
    description: "Solve water drainage issues on your property",
    benefit: "Protect your home from water damage",
    image: serviceRoofCleaning,
    image400: serviceRoofCleaning400,
    slug: "drainage",
  },
  {
    icon: Flower2,
    title: "Seasonal Color Installation",
    description: "Beautiful seasonal flowers and plantings",
    benefit: "Keep your property vibrant year-round",
    image: serviceWindowCleaning,
    image400: serviceWindowCleaning400,
    slug: "seasonal-color",
  },
  {
    icon: Leaf,
    title: "Property Clean-Up & Rejuvenation",
    description: "Complete yard clean-up and landscape restoration",
    benefit: "Restore your property's curb appeal",
    image: serviceDriveway,
    image400: serviceDriveway400,
    slug: "property-cleanup",
  },
  {
    icon: Paintbrush,
    title: "Landscape Rejuvenation",
    description: "Breathe new life into existing landscapes",
    benefit: "Revitalize tired or overgrown landscaping",
    image: serviceDeckFence,
    image400: serviceDeckFence400,
    slug: "landscape-rejuvenation",
  },
];

const ServicesPreview = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="section-padding bg-background">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 sm:mb-12"
        >
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3 sm:mb-4">
            What <span className="text-primary">Professional</span> Services Do We Offer?
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto px-4">
            Comprehensive landscape and irrigation services for homes and businesses in Bella Vista, Rogers, Bentonville & NWA
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <a 
                href={`/services#${service.slug}`}
                className={`group block rounded-lg border overflow-hidden transition-all duration-300 hover:shadow-xl ${
                  service.featured
                    ? "border-primary bg-card"
                    : "border-border bg-card hover:border-primary/50"
                }`}
              >
                {/* Service Image */}
                <div className="relative h-36 sm:h-40 overflow-hidden">
                  <img
                    src={service.image}
                    srcSet={`${service.image400} 400w, ${service.image} 480w`}
                    sizes="(max-width: 640px) 100vw, 400px"
                    alt={`Professional ${service.title.toLowerCase()} service in Northwest Arkansas`}
                    width={400}
                    height={160}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className={`absolute inset-0 ${
                    service.featured 
                      ? "bg-gradient-to-t from-primary/80 to-primary/20" 
                      : "bg-gradient-to-t from-charcoal/70 to-transparent"
                  }`} />
                  <div className="absolute bottom-3 left-4">
                    <service.icon className={`h-8 w-8 ${service.featured ? "text-accent-light" : "text-accent-light"}`} />
                  </div>
                  {service.featured && (
                    <div className="absolute top-3 right-3 bg-accent text-accent-foreground text-xs font-bold px-2 py-1 rounded">
                      POPULAR
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-5">
                  <h3 className="font-heading text-lg font-bold text-foreground mb-1">
                    {service.title}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-2">
                    {service.description}
                  </p>
                  <p className="text-primary-deep text-xs font-medium flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-accent rounded-full" />
                    {service.benefit}
                  </p>
                </div>
              </a>
            </motion.div>
          ))}
        </div>

        {/* Commercial Services Teaser */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-8 sm:mt-12 p-6 sm:p-8 bg-charcoal rounded-lg text-center"
        >
          <Building className="h-10 w-10 sm:h-12 sm:w-12 text-accent-light mx-auto mb-3 sm:mb-4" />
          <h3 className="font-heading text-xl sm:text-2xl font-bold text-primary-foreground mb-2">
            Commercial Services Available
          </h3>
          <p className="text-primary-foreground/70 mb-4 sm:mb-6 max-w-xl mx-auto text-sm sm:text-base">
            Commercial landscape design, irrigation systems, property maintenance, and seasonal services for businesses across NWA
          </p>
          <Link to="/services">
            <Button variant="cta" size="lg" className="group">
              View All Services
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesPreview;
